import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Filter, RefreshCw, X } from "lucide-react";
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api } from "@/lib/api";
import { formatCell, inr } from "@/lib/format";
import { DEPTS } from "@/nav";
import { Kpi, Panel } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

const PIE_COLORS = ["#2563EB", "#0EA5E9", "#10B981", "#F59E0B", "#6366F1", "#EF4444", "#64748B"];

const short = (v) => {
  const a = Math.abs(v);
  if (a >= 1e7) return `${(v / 1e7).toFixed(1)}Cr`;
  if (a >= 1e5) return `${(v / 1e5).toFixed(1)}L`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return String(v);
};

const legendText = (v) => <span className="text-slate-600">{v}</span>;

const kpiValue = (k) => (k.fmt === "inr" ? inr(Math.round(k.value)) : k.value);

const DashKpi = ({ k, i }) => <Kpi testId={`dash-kpi-${i}`} label={k.label} value={kpiValue(k)} sub={k.sub} color={i} delay={i * 40} />;

const axes = (c) => [
  <CartesianGrid key="g" strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />,
  <XAxis key="x" dataKey={c.x} fontSize={10} interval="preserveStartEnd" minTickGap={8} tickFormatter={(v) => (String(v).length > 16 ? `${String(v).slice(0, 15)}…` : v)} />,
  <YAxis key="y" fontSize={10} width={48} tickFormatter={c.money ? short : undefined} />,
  <Tooltip key="t" formatter={(v) => (c.money ? inr(v) : v)} />,
  <Legend key="l" wrapperStyle={{ fontSize: 11 }} formatter={legendText} />,
];

const ChartBody = ({ c }) => {
  if (c.type === "pie") {
    return (
      <PieChart>
        <Pie data={c.data} dataKey="value" nameKey="name" innerRadius="45%" outerRadius="75%" paddingAngle={2}>
          {c.data.map((d, i) => <Cell key={d.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
        </Pie>
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v, e) => <span className="text-slate-600">{v} ({e.payload.value})</span>} />
      </PieChart>
    );
  }
  if (c.type === "composed") {
    return (
      <ComposedChart data={c.data}>
        {axes(c)}
        {c.series.map((s) => (s.key === "cumulative"
          ? <Line key={s.key} dataKey={s.key} name={s.label} stroke="#E11D48" strokeWidth={2} dot />
          : <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[3, 3, 0, 0]} />))}
      </ComposedChart>
    );
  }
  return (
    <BarChart data={c.data}>
      {axes(c)}
      {c.series.map((s) => <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[3, 3, 0, 0]} />)}
    </BarChart>
  );
};

const ChartPanel = ({ c, i }) => (
  <Panel title={c.title} color={i} testId={`dash-chart-${c.id}`} className={`min-w-0 ${c.span === 2 ? "lg:col-span-2" : ""}`}>
    <div className="h-64 p-2">
      {c.data.length ? <ResponsiveContainer><ChartBody c={c} /></ResponsiveContainer>
        : <p className="pt-24 text-center text-xs text-slate-400">No data yet</p>}
    </div>
  </Panel>
);

const Cell2 = ({ col, v }) => (col.type === "status" ? <StatusBadge value={v} /> : formatCell(col.type, v));

const DashTable = ({ t }) => (
  <Panel title={`${t.title} (${t.rows.length})`} testId={`dash-table-${t.id}`} className="min-w-0 lg:col-span-3">
    <div className="max-h-80 overflow-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0 z-10 text-left text-[11px] uppercase tracking-wider">
          <tr>{t.columns.map((c) => <th key={c.key} className="whitespace-nowrap px-3 py-2">{c.label}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {t.rows.map((r) => (
            <tr key={r.id || r.code || r.material || r.invoice_no || r.bill_no}>
              {t.columns.map((c) => <td key={c.key} className="whitespace-nowrap px-3 py-1.5"><Cell2 col={c} v={r[c.key]} /></td>)}
            </tr>
          ))}
          {!t.rows.length && <tr><td colSpan={t.columns.length} className="px-3 py-6 text-center text-slate-400">Nothing to show</td></tr>}
        </tbody>
      </table>
    </div>
  </Panel>
);

const FIELD = "h-8 rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-800 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100";

const FilterBar = ({ f, setF, sites }) => {
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const active = f.site || f.month_from || f.month_to;
  return (
    <div className="relative mt-5 flex flex-wrap items-end gap-3 rounded-xl bg-white/10 px-3 py-2.5 ring-1 ring-white/15 backdrop-blur" data-testid="dash-filter-bar">
      <Filter className="mb-2 h-4 w-4 text-blue-100" />
      <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-blue-100">Site
        <select value={f.site} onChange={set("site")} className={`${FIELD} min-w-[170px]`} data-testid="dash-filter-site">
          <option value="">All sites</option>
          {sites.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-blue-100">From month
        <input type="month" value={f.month_from} max={f.month_to || undefined} onChange={set("month_from")} className={FIELD} data-testid="dash-filter-month-from" />
      </label>
      <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-blue-100">To month
        <input type="month" value={f.month_to} min={f.month_from || undefined} onChange={set("month_to")} className={FIELD} data-testid="dash-filter-month-to" />
      </label>
      {active && (
        <Button size="sm" variant="ghost" className="h-8 text-xs text-white hover:bg-white/15 hover:text-white" onClick={() => setF({ site: "", month_from: "", month_to: "" })} data-testid="dash-filter-clear">
          <X className="mr-1 h-3.5 w-3.5" />Clear filters
        </Button>
      )}
      <p className="ml-auto self-center text-[11px] text-blue-100/80">Site applies to site-linked records; month uses each record's date</p>
    </div>
  );
};

const TODAY = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

export default function DeptDashboard() {
  const { dept } = useParams();
  const info = DEPTS.find((d) => d.id === dept);
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [tick, setTick] = useState(0);
  const [f, setF] = useState({ site: "", month_from: "", month_to: "" });
  const [sites, setSites] = useState([]);

  useEffect(() => {
    let live = true;
    setD(null);
    setErr(false);
    api.get(`/dept-dashboard/${dept}`, { params: f })
      .then((r) => { if (live) { setD(r.data); setSites(r.data.filters.sites); } })
      .catch(() => { if (live) { setErr(true); } });
    return () => { live = false; };
  }, [dept, tick, f]);
  const load = () => setTick((t) => t + 1);

  if (!info) return <Navigate to="/" replace />;
  return (
    <div data-testid={`dept-dashboard-${dept}`} className="space-y-4">
      <section data-testid="dept-hero" className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1E64B4] via-[#17549C] to-[#0B3D7A] p-5 text-white shadow-xl shadow-blue-900/20 fade-up">
        <span className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-white/10" />
        <span className="pointer-events-none absolute -bottom-24 right-40 h-48 w-48 rounded-full bg-white/5" />
        <div className="relative flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20"><info.icon className="h-5 w-5" /></span>
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h1 data-testid="page-title" className="font-heading text-2xl font-bold sm:text-3xl">{info.name} Dashboard</h1>
                <span className="rounded-full bg-white/15 px-3 py-1 text-[11px] ring-1 ring-white/20">{TODAY}</span>
              </div>
              <p className="mt-1 text-sm text-blue-100">Live figures computed from all department modules</p>
            </div>
          </div>
          <Button size="sm" onClick={load} className="bg-white/15 text-white ring-1 ring-white/25 hover:bg-white/25" data-testid="dash-refresh-btn"><RefreshCw className="mr-1 h-3.5 w-3.5" />Refresh</Button>
        </div>
        <FilterBar f={f} setF={setF} sites={sites} />
      </section>
      {err && <div data-testid="dash-error" className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">Could not load dashboard. Try Refresh.</div>}
      {!d && !err && <div className="text-sm text-slate-500">Loading...</div>}
      {d && (
        <>
          <div className={`grid grid-cols-2 gap-3 md:grid-cols-3 ${d.kpis.length === 6 ? "xl:grid-cols-6" : "xl:grid-cols-5"}`}>{d.kpis.map((k, i) => <DashKpi key={k.label} k={k} i={i} />)}</div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {d.charts.map((c, i) => <ChartPanel key={c.id} c={c} i={i} />)}
            {d.tables.map((t) => <DashTable key={t.id} t={t} />)}
          </div>
        </>
      )}
    </div>
  );
}
